import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from './redux/store';
import Navbar from './components/Navbar';
import HeroSection from './components/HeroSection';
import FeaturesSection from './components/FeaturesSection';
import CTASection from './components/CTASection';
import Footer from './components/Footer';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import InfoPage from './pages/InfoPage';
import AdminDashboard from './pages/admin/AdminDashboard';
import CustomerDashboardLayout from './components/layout/CustomerDashboardLayout';
import AdminLayout from './components/admin/AdminLayout';
import AdminSidebar from './components/sidebar/AdminSidebar';
import ProfileManagementPage from './pages/customer/ProfileManagementPage';
import DocumentUploadPage from './pages/customer/DocumentUploadPage';
import PaymentSubmissionPage from './pages/customer/PaymentSubmissionPage';
import CustomerHomePage from './pages/customer/HomePage';
import MyPaymentsPage from './pages/customer/MyPaymentsPage';
import PaymentDetailsPage from './pages/customer/PaymentDetailsPage';
import ChangePasswordPage from './pages/customer/ChangePasswordPage';
import FundingPage from './pages/customer/FundingPage';
import PendingRegistrationsPage from './pages/admin/PendingRegistrationsPage';
import DocumentApprovalsPage from './pages/admin/DocumentApprovalsPage';
import PaymentRequestsPage from './pages/admin/PaymentRequestsPage';
import AdminPaymentsPage from './pages/admin/AdminPaymentsPage';
import AdminActionPage from './pages/admin/AdminActionPage';
import AdminReviewPage from './pages/admin/AdminReviewPage';
import AdminProfilePage from './pages/admin/AdminProfilePage';
import UserManagementPage from './pages/admin/UserManagementPage';
import BankAccountPage from './pages/admin/BankAccountPage';
import DeleteUnverifiedUsersPage from './pages/admin/DeleteUnverifiedUsersPage';
import SimulationPage from './pages/admin/SimulationPage';
import VirtualAccountsPage from './pages/admin/VirtualAccountsPage';
import LoginModal from './components/LoginModal';
import SignupModal from './components/SignupModal';
import ForgotPasswordModal from './components/ForgotPasswordModal';
import EmailVerificationModal from './components/EmailVerificationModal';
import ProtectedRoute from './components/ProtectedRoute';

import { ErrorBoundary } from './components/ErrorBoundary/ErrorBoundary';
import { restoreAuth, setRestoring, setVerifying, verifyAuth, logout } from './redux/authSlice';
import OhMyFinAISuite from './components/OhMyFinAI/OhMyFinAISuite';
import SlidingImageBackdrop from './components/layout/SlidingImageBackdrop';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './App.css';
import './styles/global.css';
import './styles/enterpriseDashboard.css';

const HomePage: React.FC<{ onSignupClick: () => void }> = ({ onSignupClick }) => {
  return (
    <>
      <HeroSection onGetStartedClick={onSignupClick} />
      <FeaturesSection />
      <CTASection onOpenAccountClick={onSignupClick} />
    </>
  );
};

// Component to conditionally render footer based on route
const ConditionalFooter: React.FC = () => {
  const location = useLocation();
  
  // Don't show footer on dashboard routes
  const isDashboardRoute = 
    location.pathname.startsWith('/dashboard') ||
    location.pathname.startsWith('/admin') ||
    location.pathname.startsWith('/user-dashboard') ||
    location.pathname.startsWith('/admin-dashboard');
  
  return !isDashboardRoute ? <Footer /> : null;
};

const ConditionalNavbar: React.FC<{ onLoginClick: () => void; onSignupClick: () => void }> = ({ onLoginClick, onSignupClick }) => {
  const location = useLocation();
  const isDashboardRoute =
    location.pathname.startsWith('/dashboard') ||
    location.pathname.startsWith('/admin') ||
    location.pathname.startsWith('/user-dashboard') ||
    location.pathname.startsWith('/admin-dashboard');

  if (isDashboardRoute) {
    return null;
  }

  return <Navbar onLoginClick={onLoginClick} onGetStartedClick={onSignupClick} />;
};

const ScrollToTop: React.FC = () => {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const target = document.querySelector(hash);
      if (target) {
        target.scrollIntoView({ block: 'start' });
        return;
      }
    }

    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [pathname, hash]);

  return null;
};

const AppContainer: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const isDashboardRoute =
    location.pathname.startsWith('/dashboard') ||
    location.pathname.startsWith('/admin') ||
    location.pathname.startsWith('/user-dashboard') ||
    location.pathname.startsWith('/admin-dashboard');

  return (
    <div className={`App ${isDashboardRoute ? 'app-dashboard' : 'app-public'}`}>
      <SlidingImageBackdrop variant={isDashboardRoute ? 'dashboard' : 'public'} />
      <div className="app-content-layer">{children}</div>
    </div>
  );
};

const App: React.FC = () => {
  const [showLoginModal, setShowLoginModal] = React.useState(false);
  const [showSignupModal, setShowSignupModal] = React.useState(false);
  const [showForgotPasswordModal, setShowForgotPasswordModal] = React.useState(false);
  const [showEmailVerificationModal, setShowEmailVerificationModal] = React.useState(false);
  const [verificationEmail, setVerificationEmail] = React.useState('');
  const { user } = useSelector((state: RootState) => state.auth);
  const dispatch = useDispatch<AppDispatch>();

  // Restore authentication state from sessionStorage on app load
  useEffect(() => {
    const bootstrapAuth = async () => {
      dispatch(setRestoring(true));

      const storedUser = sessionStorage.getItem('user');
      const storedToken = sessionStorage.getItem('token');
      const isAuthenticated = sessionStorage.getItem('isAuthenticated') === 'true';

      if (!storedUser || !storedToken || !isAuthenticated) {
        dispatch(setRestoring(false));
        return;
      }

      try {
        const user = JSON.parse(storedUser);
        dispatch(restoreAuth({ user, token: storedToken }));

        // Verify token with backend. Keep local session on transient network/server issues.
        dispatch(setVerifying(true));
        const { authApi } = await import('./services/api');
        const data = await authApi.verify();
        const verifiedUser = data.data.user;
        if (!verifiedUser) {
          throw new Error('User data missing during token verification');
        }
        dispatch(verifyAuth({ user: verifiedUser, token: storedToken }));
      } catch (error: any) {
        console.error('Token verification failed:', error);
        if (error.response?.status === 401) {
          dispatch(logout());
        }
      } finally {
        dispatch(setVerifying(false));
        dispatch(setRestoring(false));
      }
    };

    bootstrapAuth();
  }, [dispatch]);

  const handleLoginClick = () => {
    setShowLoginModal(true);
  };

  const handleSignupClick = () => {
    setShowSignupModal(true);
  };

  const handleCloseModals = () => {
    setShowLoginModal(false);
    setShowSignupModal(false);
    setShowForgotPasswordModal(false);
    setShowEmailVerificationModal(false);
  };

  return (
    <ErrorBoundary>
      <Router>
        <ScrollToTop />
        <AppContainer>
        <ConditionalNavbar
          onLoginClick={handleLoginClick}
          onSignupClick={handleSignupClick}
        />
        <Routes>
          <Route path="/" element={<HomePage onSignupClick={handleSignupClick} />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/ohmyfin" element={<OhMyFinAISuite isAdmin={user?.role === 'admin'} />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route
            path="/careers"
            element={
              <InfoPage
                title="Careers"
                subtitle="Build the future of global procurement with us."
                sections={[
                  {
                    heading: 'Why Kenluk',
                    body:
                      'We are a remote-friendly team building reliable payment infrastructure for global trade.',
                    items: ['Ownership and impact', 'Modern tech stack', 'Growth-focused culture'],
                  },
                  {
                    heading: 'Open Roles',
                    body: 'We are always looking for engineers, designers, and operations experts.',
                    items: ['Frontend Engineer', 'Backend Engineer', 'Product Designer'],
                  },
                ]}
              />
            }
          />
          <Route
            path="/press"
            element={
              <InfoPage
                title="Press"
                subtitle="News, media resources, and company updates."
                sections={[
                  {
                    heading: 'Press Kit',
                    body:
                      'Access logos, brand guidelines, and product screenshots for media coverage.',
                    items: ['Brand assets', 'Product screenshots', 'Leadership bios'],
                  },
                  {
                    heading: 'Media Inquiries',
                    body:
                      'Reach out for interviews, product announcements, or partnership stories.',
                    items: ['Email press@kenluk.com', 'Response within 48 hours'],
                  },
                ]}
              />
            }
          />
          <Route
            path="/faq"
            element={
              <InfoPage
                title="FAQ"
                subtitle="Answers to the most common questions about Kenluk Pay."
                sections={[
                  {
                    heading: 'Account & Onboarding',
                    body:
                      'Create an account, complete verification, and start paying vendors globally.',
                    items: ['How long does verification take?', 'What documents are required?'],
                  },
                  {
                    heading: 'Payments',
                    body:
                      'Submit international payment requests, track status, and get real-time updates.',
                    items: ['How are rates calculated?', 'When do payments settle?'],
                  },
                ]}
              />
            }
          />
          <Route
            path="/help"
            element={
              <InfoPage
                title="Help Center"
                subtitle="Guides and resources to help you move faster."
                sections={[
                  {
                    heading: 'Getting Started',
                    body:
                      'Step-by-step guidance for setting up your account and first payment.',
                    items: ['Create your profile', 'Upload verification documents', 'Submit a payment'],
                  },
                  {
                    heading: 'Support',
                    body: 'Need help? Our team is ready to assist.',
                    items: ['Live chat in the dashboard', 'Email support@kenluk.com'],
                  },
                ]}
              />
            }
          />
          <Route
            path="/terms"
            element={
              <InfoPage
                title="Terms of Service"
                subtitle="Clear rules for using Kenluk Pay responsibly."
                sections={[
                  {
                    heading: 'Usage',
                    body:
                      'Use the platform in compliance with applicable laws and only for legitimate transactions.',
                  },
                  {
                    heading: 'Security',
                    body:
                      'Keep your credentials secure and report any suspected activity immediately.',
                  },
                ]}
              />
            }
          />
          <Route
            path="/privacy"
            element={
              <InfoPage
                title="Privacy Policy"
                subtitle="Your data is handled with transparency and care."
                sections={[
                  {
                    heading: 'Data Collection',
                    body:
                      'We collect only what is necessary to provide secure payments and compliance.',
                  },
                  {
                    heading: 'Your Rights',
                    body:
                      'You can request data access, corrections, or deletion at any time.',
                  },
                ]}
              />
            }
          />
          <Route
            path="/cookies"
            element={
              <InfoPage
                title="Cookies"
                subtitle="We use cookies to keep the platform secure and personalized."
                sections={[
                  {
                    heading: 'Essential Cookies',
                    body:
                      'These cookies are required for login, security, and session continuity.',
                  },
                  {
                    heading: 'Preferences',
                    body:
                      'You can manage optional analytics cookies in your browser settings.',
                  },
                ]}
              />
            }
          />
          {/* Legacy routes - redirect to new dashboard routes */}
          <Route
            path="/user-dashboard"
            element={<Navigate to="/dashboard/home" replace />}
          />
          <Route
            path="/admin-dashboard"
            element={<Navigate to="/admin/dashboard" replace />}
          />
          {/* Customer Dashboard Routes */}
          <Route
            path="/dashboard/*"
            element={
              <ProtectedRoute requiredRole="user">
                <CustomerDashboardLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="home" replace />} />
            <Route path="home" element={<CustomerHomePage />} />
            <Route path="profile" element={<ProfileManagementPage />} />
            <Route path="documents" element={<DocumentUploadPage />} />
            <Route path="funding" element={<FundingPage />} />
            <Route path="payments" element={<PaymentSubmissionPage />} />
            <Route path="my-payments" element={<MyPaymentsPage />} />
            <Route path="payment-details/:paymentId" element={<PaymentDetailsPage />} />
            <Route path="change-password" element={<ChangePasswordPage />} />
          </Route>

          {/* Admin Dashboard Routes */}
          <Route
            path="/admin/*"
            element={
              <ProtectedRoute requiredRole="admin">
                <AdminLayout sidebar={<AdminSidebar />} />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="dashboard" replace />} />
            <Route path="dashboard" element={<AdminDashboard />} />
            <Route path="pending-registrations" element={<PendingRegistrationsPage />} />
            <Route path="document-approvals" element={<DocumentApprovalsPage />} />
            <Route path="payment-requests" element={<PaymentRequestsPage />} />
            <Route path="admin-payments" element={<AdminPaymentsPage />} />
            <Route path="admin-action" element={<AdminActionPage />} />
            <Route path="admin-review" element={<AdminReviewPage />} />
            <Route path="profile" element={<AdminProfilePage />} />
            <Route path="user-management" element={<UserManagementPage />} />
            <Route path="delete-unverified-users" element={<DeleteUnverifiedUsersPage />} />
            <Route path="bank-account" element={<BankAccountPage />} />
            <Route path="simulations" element={<SimulationPage />} />
            <Route path="virtual-accounts" element={<VirtualAccountsPage />} />
          </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <ConditionalFooter />
        
        {showLoginModal && (
          <LoginModal 
            onClose={handleCloseModals} 
            onSwitchToSignup={() => {
              setShowLoginModal(false);
              setShowSignupModal(true);
            }} 
            onForgotPassword={() => {
              setShowLoginModal(false);
              setShowForgotPasswordModal(true);
            }}
          />
        )}
        {showSignupModal && (
          <SignupModal
            onClose={handleCloseModals}
            onSwitchToLogin={() => {
              setShowSignupModal(false);
              setShowLoginModal(true);
            }}
            onSwitchToVerification={(email) => {
              setVerificationEmail(email);
              setShowSignupModal(false);
              setShowEmailVerificationModal(true);
            }}
          />
        )}
        {showForgotPasswordModal && (
          <ForgotPasswordModal 
            onClose={handleCloseModals}
            onSwitchToLogin={() => {
              setShowForgotPasswordModal(false);
              setShowLoginModal(true);
            }}
          />
        )}
        {showEmailVerificationModal && (
          <EmailVerificationModal
            onClose={handleCloseModals}
            onSwitchToLogin={() => {
              setShowEmailVerificationModal(false);
              setShowLoginModal(true);
            }}
            email={verificationEmail}
          />
        )}
        {/* IssueReporter removed per request */}
        <ToastContainer

          position="top-right"
          autoClose={4500}
          newestOnTop
          closeOnClick
          pauseOnHover
          pauseOnFocusLoss={false}
          theme="colored"
        />
      </AppContainer>
      </Router>
    </ErrorBoundary>
  );
};

export default App;
